import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { aggregateByPeriod, buildPeriods } from './timephased-grid.js';
import { formatHours } from './format.js';
import type { UsageGridProps } from './types.js';

/**
 * 資源使用狀況／任務使用狀況共用的階層＋逐時段格線實作。
 * 純展示元件：props in / callbacks out，無 store／Tauri 相依。
 * 用「攤平列表＋切片」做虛擬捲動，與 grid/TaskGrid.tsx 相同手法（各自獨立實作，
 * 避免跨目錄所有權相依）。
 */

const OVERSCAN_ROWS = 6;
const FALLBACK_VIEWPORT_ROWS = 20;
const PERIOD_COL_WIDTH = 64;
const LABEL_COL_WIDTH = 220;

interface FlatRow {
  readonly kind: 'group' | 'child';
  readonly key: string;
  readonly label: string;
  readonly values: readonly { hours: number; overallocated: boolean }[];
  readonly groupId: string;
}

export interface UsageGridBaseProps extends UsageGridProps {
  readonly testIdPrefix: string;
  readonly groupLabelHeader: string;
}

export function UsageGridBase(props: UsageGridBaseProps): React.JSX.Element {
  const {
    groups,
    rangeStart,
    rangeEnd,
    granularity,
    onGranularityChange,
    collapsedGroupIds,
    onToggleCollapse,
    rowHeight = 28,
    testIdPrefix,
    groupLabelHeader,
  } = props;

  const bodyRef = useRef<HTMLDivElement>(null);
  const [viewportHeight, setViewportHeight] = useState(0);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setViewportHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const periods = useMemo(
    () => buildPeriods(rangeStart, rangeEnd, granularity),
    [rangeStart, rangeEnd, granularity],
  );

  const collapsedSet = useMemo(() => new Set(collapsedGroupIds), [collapsedGroupIds]);

  const flatRows = useMemo<readonly FlatRow[]>(() => {
    const out: FlatRow[] = [];
    for (const group of groups) {
      const groupDaily = group.children.flatMap((child) => child.daily);
      out.push({
        kind: 'group',
        key: group.id,
        label: group.label,
        groupId: group.id,
        values: aggregateByPeriod(groupDaily, periods, granularity, rangeStart, rangeEnd),
      });
      if (collapsedSet.has(group.id)) continue;
      for (const child of group.children) {
        out.push({
          kind: 'child',
          key: `${group.id}::${child.id}`,
          label: child.label,
          groupId: group.id,
          values: aggregateByPeriod(child.daily, periods, granularity, rangeStart, rangeEnd),
        });
      }
    }
    return out;
  }, [groups, periods, granularity, collapsedSet, rangeStart, rangeEnd]);

  const effectiveViewportHeight = viewportHeight || FALLBACK_VIEWPORT_ROWS * rowHeight;
  const [scrollTop, setScrollTop] = useState(0);
  const totalHeight = flatRows.length * rowHeight;
  // 列數可能因收合／篩選而在兩次渲染間驟減；`scrollTop` 是獨立的 state,不會隨之自動
  // 修正,若不夾住就可能算出超出新列數範圍的 startIndex,導致 slice() 回傳空陣列
  // （畫面看起來像資料整個消失）。夾在 [0, totalHeight] 內確保 startIndex 永遠落在
  // 目前實際存在的列範圍內。
  const safeScrollTop = Math.min(Math.max(0, scrollTop), totalHeight);
  const startIndex = Math.max(0, Math.floor(safeScrollTop / rowHeight) - OVERSCAN_ROWS);
  const visibleRowCount = Math.ceil(effectiveViewportHeight / rowHeight) + OVERSCAN_ROWS * 2;
  const endIndex = Math.min(flatRows.length, startIndex + visibleRowCount);
  const renderedRows = flatRows.slice(startIndex, endIndex);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  const gridColumnsWidth = LABEL_COL_WIDTH + periods.length * PERIOD_COL_WIDTH;

  return (
    <div data-testid={`${testIdPrefix}-root`} className="flex h-full w-full flex-col bg-surface text-sm text-fg">
      <div className="flex items-center gap-2 border-b border-line-strong px-2 py-1.5">
        <span className="font-medium">{groupLabelHeader}</span>
        <div className="ml-auto flex gap-1" role="group" aria-label="期間粒度">
          {(['Day', 'Week', 'Month'] as const).map((g) => (
            <button
              key={g}
              type="button"
              data-testid={`${testIdPrefix}-granularity-${g}`}
              aria-pressed={granularity === g}
              className={[
                'rounded px-2 py-0.5',
                granularity === g
                  ? 'bg-blue-600 text-white'
                  : 'bg-raised text-fg',
              ].join(' ')}
              onClick={() => onGranularityChange(g)}
            >
              {g === 'Day' ? '日' : g === 'Week' ? '週' : '月'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex overflow-hidden border-b border-line-strong bg-raised font-medium">
        <div className="shrink-0 border-r border-line px-2 py-1" style={{ width: LABEL_COL_WIDTH }}>
          {groupLabelHeader}
        </div>
        {periods.map((period) => (
          <div
            key={period.start}
            className="shrink-0 border-r border-line px-1 py-1 text-center"
            style={{ width: PERIOD_COL_WIDTH }}
          >
            {period.label}
          </div>
        ))}
      </div>

      <div ref={bodyRef} data-testid={`${testIdPrefix}-body`} className="relative flex-1 overflow-auto" onScroll={handleScroll}>
        <div style={{ height: totalHeight, width: gridColumnsWidth, position: 'relative' }}>
          {renderedRows.map((row, offset) => {
            const rowIndex = startIndex + offset;
            return (
              <div
                key={row.key}
                role="row"
                data-testid={`${testIdPrefix}-row-${row.key}`}
                className={[
                  'absolute left-0 right-0 flex border-b border-hairline',
                  row.kind === 'group' ? 'bg-raised font-medium' : '',
                ].join(' ')}
                style={{ top: rowIndex * rowHeight, height: rowHeight }}
              >
                <div
                  className="flex shrink-0 items-center gap-1 overflow-hidden border-r border-hairline px-2"
                  style={{ width: LABEL_COL_WIDTH, paddingLeft: row.kind === 'child' ? 24 : 8 }}
                >
                  {row.kind === 'group' ? (
                    <button
                      type="button"
                      aria-label={collapsedSet.has(row.groupId) ? '展開' : '收合'}
                      data-testid={`${testIdPrefix}-toggle-${row.groupId}`}
                      className="w-3 shrink-0 text-fg-muted"
                      onClick={() => onToggleCollapse(row.groupId)}
                    >
                      {collapsedSet.has(row.groupId) ? '▶' : '▼'}
                    </button>
                  ) : null}
                  <span className="truncate">{row.label}</span>
                </div>
                {row.values.map((value, colIndex) => (
                  <div
                    key={colIndex}
                    data-testid={`${testIdPrefix}-cell-${row.key}-${colIndex}`}
                    data-overallocated={value.overallocated}
                    className={[
                      'shrink-0 border-r border-hairline px-1 py-1 text-right',
                      value.overallocated ? 'bg-red-100 text-red-900 dark:bg-danger-soft dark:text-danger' : '',
                    ].join(' ')}
                    style={{ width: PERIOD_COL_WIDTH }}
                  >
                    {formatHours(value.hours)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default UsageGridBase;
